import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';

import Button from 'components/Button';
import { useCheckout } from 'features/organizations/memberships/checkout/context';
import { CheckoutSteps } from 'features/organizations/memberships/checkout/types';
import useProductTimeGateText from 'hooks/useProductTimeGateText';

const ConfirmOrderButton = () => {
  const { t } = useTranslation(['organizations']);
  const { goToStep, cart, checkoutSession, paymentMethod } = useCheckout();
  const product =
    cart.selectedProducts[Object.keys(cart.selectedProducts)[0]]?.productData;
  const timeGateText = useProductTimeGateText(
    {
      validFrom: product.validFrom,
      validUntil: product.validUntil
    },
    true
  );
  const total = product.product.price || 0;

  // Block the user from submitting if
  // - Checkout is busy.
  // - User has selected paid tickets AND checkout session has not been assigned a card yet.
  // - User has selected a payment method other than card.
  const isDisabled =
    Boolean(checkoutSession.checkoutError) ||
    Boolean(checkoutSession.isCheckoutDisabled) ||
    checkoutSession.isLoadingCheckout ||
    (checkoutSession?.sessionData?.total > 0 &&
      !paymentMethod?.selectedCard?.value &&
      !paymentMethod.selectedBuyNowPayLater.value) ||
    cart.isEmpty;

  const onConfirm = () => {
    checkoutSession.confirmCheckout().then(() => {
      goToStep(CheckoutSteps.Success);
    });
  };

  const getButtonText = () => {
    if (checkoutSession?.sessionData?.total === 0) {
      return t('checkout:confirmAndJoin');
    }

    if (paymentMethod.selectedBuyNowPayLater.value) {
      return t('checkout:continueTo', {
        paymentMethod: capitalize(paymentMethod.selectedBuyNowPayLater.value)
      });
    }

    return t('checkout:confirmAndPay');
  };

  const buttonText = getButtonText();

  return (
    <>
      {checkoutSession.checkoutError?.message && (
        <div className="text-error font-normal text-base text-center pb-4 max-h-[60px] overflow-y-auto">
          {checkoutSession.checkoutError.message}
        </div>
      )}
      <Button
        variant="primaryDark"
        size="large"
        className="w-full mt-0!"
        loading={checkoutSession.isLoadingCheckout}
        onClick={onConfirm}
        disabled={isDisabled}
      >
        {buttonText}
      </Button>
      <p className="text-sm font-normal text-gray-500 text-center pt-3">
        {total > 0 ? (
          <>{t('organizations:memberships.checkout.paymentTimeDisclaimer')} </>
        ) : (
          <>{t('organizations:memberships.checkout.paymentFreeDisclaimer')} </>
        )}
        {timeGateText && <>{timeGateText} </>}
        {t('organizations:memberships.checkout.paymentDisclaimer')}
      </p>
    </>
  );
};

export default ConfirmOrderButton;
